import { Button, message } from 'antd'
import { addCommasToNumber } from 'helpers'
import { getAuthToken } from 'helpers/FetchHelper'
import RouteList from 'pages/Routes'
import SelectFiat from 'pages/Send/components/SelectFiat'
import SelectToken from 'pages/Send/components/SelectToken'
import SwapItem from 'pages/Send/components/SwapItem'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTokenStore } from 'state/useTokenStore'
import { useTranslation } from 'translations'
import { renderValue } from 'utils'
import useSWRMutation from 'swr/mutation'
import SWR_KEY from 'services/swr/constant'
import handle from 'services/Axios'
import { useGetFiatList, useGetTokenList } from 'services/swr/token'
import moment from 'moment'

const SwapBox = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [countdown, setCountdown] = useState<number>(0)

	const tokenFrom = useTokenStore((s) => s.tokenFrom)
	const fiatTo = useTokenStore((s) => s.fiatTo)

	const { mutate: refreshFiat, isValidating: isLoadingFiat } = useGetFiatList()
	const { mutate: refreshToken, isValidating: isLoadingToken } = useGetTokenList()

	const [openSelectModal, setOpenSelectModal] = useState<string | undefined>()
	const [amount, setAmount] = useState<string>("")
	const [typeSendAmount, setTypeSendAmount] = useState<"TOKEN" | "FIAT">("TOKEN")
	const [isCalculating, setIsCalculating] = useState(false)
	const [convertData, setConvertData] = useState({
		amount: 0,
		rate: 1,
		feePercent: 0,
		tokenAmount: 0,
		fiatAmount: 0,
		fiatReceivedAmount: 0,
		aloFeeAmount: 0
	})

	const toggleSelectModal = (type: string | undefined) => setOpenSelectModal(type)

	const { trigger: calculateAmount } = useSWRMutation(
		SWR_KEY.CONVERT_AMOUNT,
		async (url: string, { arg }) => {
			try {
				setIsCalculating(true)
				const res = await handle.post(url, arg)
				setIsCalculating(false)
				const _data = res.data.data
				setConvertData({
					amount: typeSendAmount === "TOKEN" ? +_data.amount : +_data.converted_amount,
					rate: +_data.rate,
					feePercent: +_data.fee_percent,
					tokenAmount: +_data.token_amount_before_fee,
					fiatAmount: +_data.fiat_amount_before_fee,
					fiatReceivedAmount: +_data.fiat_amount_after_fee,
					aloFeeAmount: +_data.alo_fee_amount
				})
			} catch (error: any) {
				setIsCalculating(false)
				message.error(error.message)
			}
			return
		},
	)

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const sendActive = typeSendAmount === 'TOKEN'
	const recvActive = typeSendAmount === 'FIAT'

	const youSendValue = useMemo(() => {
		return sendActive ? (amount ?? "") : addCommasToNumber(convertData.tokenAmount)
	}, [sendActive, amount, convertData.tokenAmount])

	const recipientValue = useMemo(() => {
		return recvActive ? (amount ?? "") : addCommasToNumber(convertData.fiatAmount)
	}, [recvActive, amount, convertData.fiatAmount])

	const handleConfirm = async () => {
		if (countdown > 0) return navigate(RouteList.send)
		Promise.all([refreshFiat(), refreshToken()])
	}

	/**
	 * Effects
	 * ================================================================	
	 */

	useEffect(() => {
		if (!tokenFrom?.updated_at) return;

		const updatedAt = moment(tokenFrom.updated_at);
		const now = moment();
		const expiration = updatedAt.clone().add(5, 'minutes');
		const fiveMinutesAgo = now.clone().subtract(5, 'minutes');

		// Nếu updated_at cách đây hơn 5 phút, không reset countdown
		if (updatedAt.isSameOrBefore(fiveMinutesAgo)) {
			return;
		}

		// Nếu hiện tại còn trong vòng 5 phút kể từ updatedAt → tính countdown
		if (now.isBefore(expiration)) {
			setCountdown(Math.ceil(expiration.diff(now) / 1000));
		} else {
			// Nếu đã qua expiration nhưng updatedAt không quá cũ (<= now) → reset về 5 phút
			setCountdown(5 * 60);
		}
	}, [tokenFrom?.updated_at, isLoadingToken, isLoadingToken]);

	useEffect(() => {
		if (countdown <= 0) return

		const interval = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(interval)
					return 0
				}
				return prev - 1
			})
		}, 1000)

		return () => clearInterval(interval)
	}, [countdown])

	useEffect(() => {
		if (!tokenFrom || !fiatTo) return

		if (+amount === 0) {
			setConvertData(prev => ({
				...prev,
				tokenAmount: 0,
				fiatAmount: 0,
				fiatReceivedAmount: 0
			}))
			return;
		}

		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => {
			const n = Number(amount || 0)

			calculateAmount({
				token_id: tokenFrom.id,
				fiat_id: fiatTo.id,
				amount: String(isNaN(n) ? 0 : n),
				type: typeSendAmount === 'TOKEN' ? 'to_fiat' : 'to_token',
				type_fee: 0
			} as any)
		}, 750)

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [amount, tokenFrom, fiatTo, typeSendAmount, calculateAmount])

	return (
		<div
			className="p-4 flex flex-col gap-3 border-solid border-[1px] rounded-2xl border-black mt-6"
			style={{ boxShadow: "#0a2790 12px 12px 0px" }}
		>
			<SwapItem
				key={`send-${typeSendAmount}`}
				label={t("You send")}
				token={renderValue(tokenFrom?.symbol)}
				tokenLogo={tokenFrom?.logo}
				onClick={() => toggleSelectModal("TOKEN")}
				onChangeAmount={(v: string) => setAmount(v)}
				amount={youSendValue}
				isCalculating={!sendActive && isCalculating}
				setTypeSendAmount={setTypeSendAmount}
				typeSendAmount={typeSendAmount}
				active={sendActive}
				setConvertData={setConvertData}
			/>

			<SwapItem
				key={`recv-${typeSendAmount}`}
				label={t("Recipient gets")}
				token={renderValue(fiatTo?.name)}
				tokenLogo={fiatTo?.logo}
				onClick={() => toggleSelectModal("FIAT")}
				onChangeAmount={(v: string) => setAmount(v)}
				amount={recipientValue}
				isCalculating={!recvActive && isCalculating}
				setTypeSendAmount={setTypeSendAmount}
				typeSendAmount={typeSendAmount}
				active={recvActive}
				setConvertData={setConvertData}
			/>

			<div className="flex items-center justify-between gap-2">
				<p className="text-[#707A8A]">{t("Exchange rate")}</p>
				<p className="text-right">
					<span className='font-bold relative bottom-[1px]'>1</span>
					{" "} {renderValue(tokenFrom?.symbol)} {" "}
					~ <span className='font-bold relative bottom-[1px]'>
						{!tokenFrom ? "--" : addCommasToNumber(tokenFrom.price_vnd)}
					</span>
					{" "} VND
				</p>
			</div>

			{
				!getAuthToken()
					? (
						<Button type="primary" className='py-5 w-full mt-4' onClick={() => navigate(RouteList.signIn)}>
							{t("Login")}
						</Button>
					)
					: (
						<Button
							type="primary"
							className='py-5 w-full mt-4'
							onClick={handleConfirm}
							loading={isLoadingFiat || isLoadingToken}
						>
							{countdown <= 0 ? t("refreshPrice") : t("Send now")} {countdown > 0 ? `(${countdown}s)` : ""}
						</Button>
					)
			}

			<div
				className="py-2 px-3 rounded-[80px] flex items-center justify-center gap-2 bg-[#f5f6f8] w-fit mx-auto"
				onClick={() => navigate(RouteList.signUp)}
			>
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#FF880A" viewBox="0 0 25 24" color="#FF880A"><path fillRule="evenodd" d="m5.96 4.12 5.5-2.06c.57-.21 1.51-.21 2.07.01l5.5 2.06c1.05.39 1.91 1.64 1.91 2.76v8.1c0 .81-.53 1.87-1.18 2.36l-5.5 4.11c-.97.73-2.56.73-3.53 0l-5.5-4.11c-.65-.48-1.18-1.55-1.18-2.36v-8.1c0-1.13.86-2.37 1.91-2.77Zm4.67 9.89c.15.15.34.22.53.22s.38-.07.53-.22l4.3-4.3c.29-.29.29-.77 0-1.06a.754.754 0 0 0-1.06 0l-3.77 3.77-1.08-1.08a.755.755 0 0 0-1.06 0c-.29.29-.29.77 0 1.06l1.61 1.61Z" clipRule="evenodd"></path></svg>
				<p>{t("Free registration, 24/7 assets transfer")}</p>
			</div>

			<SelectToken open={openSelectModal === "TOKEN"} onClose={() => toggleSelectModal(undefined)} />
			<SelectFiat open={openSelectModal === "FIAT"} onClose={() => toggleSelectModal(undefined)} />
		</div>
	)
}

export default SwapBox
